import { Form, Input, Modal, notification } from 'antd';
import { IOpTableProps, OpTable } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { TABLE_HEIGHT } from 'constants/ui';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logUserActivity } from 'store/slices/userActivitySlice';
import { createVisitorWorkflow, deleteVisitorWorkflow, describeVisitorWorkflow, describeVisitorWorkflowConfig, describeVisitorWorkflowFields, describeVisitorWorkflowLocations, fetchVisitorWorkflows } from 'store/slices/visitorWorkflowsSlice';
import { AppDispatch, RootState } from 'store/store';
import { ApiResponse } from 'types/apiTypes';
import { Location } from 'types/locationTypes';
import { CreateVisitorWorkflowPayload, VisitorWorkflow } from 'types/visitorWorkflowTypes';
import { hasPermission } from 'utils/utils';

interface AttendantSignInTableProps {
    setDrawerVisible: (visible: boolean) => void;
}

const AttendantSignIn: React.FC<AttendantSignInTableProps> = ({ setDrawerVisible }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const { visitorWorkflows, fetchVisitorWorkflowsLoading } = useSelector((state: RootState) => state.visitorWorkflows);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasSigninconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:w');

    const [createForm] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [signInWorkflows, setSignInWorkflows] = useState<VisitorWorkflow[]>([]);

    const { t } = useTranslation();

    useEffect(() => {
        if (orgId) {
            dispatch(fetchVisitorWorkflows({ orgId }));
        }
    }, [dispatch, orgId]);

    // Filter and get list of active locations
    useEffect(() => {
        if (visitorWorkflows && visitorWorkflows.data) {
            const filteredWorkflows = visitorWorkflows.data.filter(visitorWorkflow => visitorWorkflow.workflowTypeId === 1);
            setSignInWorkflows(filteredWorkflows);
        }
    }, [visitorWorkflows]);

    // Handle deleting a Visitor workflow
    const handleDeleteVisitorWorkflow = async (record: VisitorWorkflow) => {
        try {
            const response = await dispatch(describeVisitorWorkflowLocations({ orgId, visitorWorkflowId: record.id }));
            const locations = response.payload as ApiResponse<Location>;
            if (locations.data.length > 0) {
                notification.error({
                    message: t('notification.error'),
                    description: t('workflow.notification.deleteBlockedError'),
                    placement: 'bottomRight',
                });
            } else {
                await dispatch(deleteVisitorWorkflow({ orgId, visitorWorkflowId: record.id }));
                await dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "80", details: record.name || '' } }))
                notification.success({
                    message: t('notification.success'),
                    description: t('workflow.notification.deleteSuccess'),
                    placement: 'bottomRight',
                });
            }
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    const handleCreateVisitorWorkflow = async (values: any) => {
        try {
            const payload: CreateVisitorWorkflowPayload = {
                name: values.name,
                workflowTypeId: 1
            };
            await dispatch(createVisitorWorkflow({ orgId, visitorWorkflow: payload }));
            await dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "70", details: values.name || '' } }))
            notification.success({
                message: t('notification.success'),
                description: t('workflow.notification.createSuccess'),
                placement: 'bottomRight',
            });
            setIsModalOpen(false);
            createForm.resetFields();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.createError'),
                placement: 'bottomRight',
            });
        }
    };

    const handleAddWorkflow = () => {
        setIsModalOpen(true);
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('workflow.attendantSignIn.table.name'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: signInWorkflows,
        columns: columns,
        rowActions: {
            onEditClick: (visitorWorkflow: VisitorWorkflow) => {
                dispatch(describeVisitorWorkflow({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowLocations({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowConfig({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowFields({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                setDrawerVisible(true);
            },
            onDeleteClick: hasSigninconfWrite ? handleDeleteVisitorWorkflow : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('workflow.attendantSignIn.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasSigninconfWrite,
        allowAddition: hasSigninconfWrite ? {
            itemName: t('workflow.attendantSignIn.itemName'),
            onClick: handleAddWorkflow,
        } : false,
        loading: fetchVisitorWorkflowsLoading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return (
        <>
            <OpTable {...opTableProps} />
            <Modal
                title={t('workflow.attendantSignIn.createWorkflowModal.title')}
                open={isModalOpen}
                onOk={createForm.submit}
                onCancel={() => setIsModalOpen(false)}
                okText={t('workflow.attendantSignIn.createWorkflowModal.ok')}
                cancelText={t('workflow.attendantSignIn.createWorkflowModal.cancel')}
                centered
            >
                <Form form={createForm} onFinish={handleCreateVisitorWorkflow}>
                    <Form.Item
                        label={t('workflow.attendantSignIn.createWorkflowModal.name')}
                        name="name"
                        rules={[{ required: true, message: t('workflow.attendantSignIn.createWorkflowModal.nameRequired') }]}
                    >
                        <Input placeholder={t('workflow.attendantSignIn.createWorkflowModal.namePlaceholder')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default AttendantSignIn;
